import React, { useCallback } from 'react';
import { BlurView } from 'expo-blur';
import { useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { Icon, theme } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { locationResultsListsV2ExpandButtonMessages as messages } from './LocationResultsListsV2ExpandButton.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListsV2ExpandButton = (
  props: LocationResultsListsV2ExpandButtonProps,
) => {
  const { onPress } = props;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  const isLocationSelectionUpdateEnabled = useFeatureFlag('CELS-5577-order-location-selection-ui-update-enabled'); // prettier-ignore

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    onPress?.();

    track('location.listview.tapped');
  }, [onPress, track]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const backgroundStyles = [styles.background, styles.backgroundUpdate];
  const touchableStyles = [
    styles.touchable,
    isLocationSelectionUpdateEnabled ? undefined : styles.touchableLegacy,
  ];

  return (
    <TouchableOpacity
      activeOpacity={ACTIVE_OPACITY}
      accessibilityRole="button"
      onPress={handleOnPress}
      aria-label={formatMessage(messages.expandButtonA11yLabel)}
      style={touchableStyles}
    >
      {isLocationSelectionUpdateEnabled ? (
        <>
          <View style={backgroundStyles} pointerEvents="none">
            <BlurView
              intensity={LOCATION_CONTAINER_BLUR_INTENSITY}
              tint="light"
              style={StyleSheet.absoluteFill}
            />
          </View>

          <View style={styles.iconContainer} pointerEvents="none">
            <Icon
              name="IconList"
              width={ICON_SIZE}
              height={ICON_SIZE}
              color={theme.colors.DARK_KALE}
            />
          </View>
        </>
      ) : (
        <Icon name="IconList" width={ICON_SIZE} height={ICON_SIZE} color={theme.colors.DARK_KALE} />
      )}
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTIVE_OPACITY = 0.8;
const ICON_SIZE = 24;
const LOCATION_CONTAINER_BLUR_INTENSITY = 85;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  touchable: {
    alignSelf: 'flex-end',
    padding: theme.spacing[2],
    borderRadius: ICON_SIZE,
  },
  touchableLegacy: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    ...theme.elevations['4'],
  },
  background: {
    ...StyleSheet.absoluteFill,
    borderRadius: ICON_SIZE,
    overflow: 'hidden',
  },
  backgroundUpdate: {
    backgroundColor: 'rgba(250, 250, 246, 0.7)',
  },
  iconContainer: {
    zIndex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type LocationResultsListsV2ExpandButtonProps = {
  onPress: () => void;
};
